import React, { useState, useRef, useEffect } from "react";
import { Bot, Sparkles, Zap, Cpu, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import gptO3Img from "@packages/assets/openai.png";
import gpt45Img from "@packages/assets/openai.png";
import claudeImg from "@packages/assets/claude.png";
import geminiImg from "@packages/assets/gemini.png";

export interface AIModel {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  activeBgColor: string;
  borderColor: string;
  activeBorderColor: string;
  description?: string;
  image?: string; // Yeni: opsiyonel image alanı
  isSelectedReverseImageColor?: boolean;
}

interface ModelButtonSelectorProps {
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  className?: string;
  isLoading?: boolean;
  loadingModelId?: string | null;
}

// ModelTooltip component for showing model descriptions
const ModelTooltip: React.FC<{
  children: React.ReactNode;
  description: string;
}> = ({ children, description }) => {
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className="w-full"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
      >
        {children}
      </div>
      {visible && (
        <div
          ref={tooltipRef}
          className="absolute left-1/2 top-full mt-2 -translate-x-1/2 mb-2 w-64 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg z-50 
          animate-fade-in-down transition-all duration-150"
        >
          <div className="p-3 text-center leading-relaxed">{description}</div>
          <div className="absolute left-1/2 top-full -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
        </div>
      )}
    </div>
  );
};

export const availableModels: AIModel[] = [
  {
    id: "gpt-o3",
    name: "GPT o3",
    icon: Sparkles,
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-50 dark:bg-green-900/20",
    activeBgColor: "bg-green-100 dark:bg-green-800/40",
    borderColor: "border-green-200 dark:border-green-700",
    activeBorderColor: "border-green-500 dark:border-green-500",
    description: "assistant.model.gpt4o.description",
    image: gptO3Img,
    isSelectedReverseImageColor: true,
  },
  {
    id: "gemini-2.5-pro",
    name: "Gemini 2.5 Pro",
    icon: Cpu,
    color: "text-amber-600 dark:text-amber-400",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
    activeBgColor: "bg-amber-100 dark:bg-amber-800/40",
    borderColor: "border-amber-200 dark:border-amber-700",
    activeBorderColor: "border-amber-500 dark:border-amber-500",
    description: "assistant.model.gpt4.description",
    image: geminiImg,
  },

  {
    id: "claude-3.7-sonnet",
    name: "Claude 3.7 Sonnet",
    icon: Bot,
    color: "text-purple-600 dark:text-purple-400",
    bgColor: "bg-purple-50 dark:bg-purple-900/20",
    activeBgColor: "bg-purple-100 dark:bg-purple-800/40",
    borderColor: "border-purple-200 dark:border-purple-700",
    activeBorderColor: "border-purple-500 dark:border-purple-500",
    description: "assistant.model.claude.description",
    image: claudeImg,
  },
  {
    id: "gpt-4.5",
    name: "GPT 4.5",
    icon: Zap,
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    activeBgColor: "bg-blue-100 dark:bg-blue-800/40",
    borderColor: "border-blue-200 dark:border-blue-700",
    activeBorderColor: "border-blue-500 dark:border-blue-500",
    description: "assistant.model.gemini.description",
    image: gpt45Img,
    isSelectedReverseImageColor: true,
  },
];

export function ModelButtonSelector({
  selectedModel,
  onModelChange,
  className = "",
  isLoading = false,
  loadingModelId = null,
}: ModelButtonSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className={`flex gap-2 w-full ${className}`}>
      {availableModels.map((model) => {
        const isSelected = model.id === selectedModel;
        const isCurrentLoading =
          isLoading &&
          (loadingModelId === model.id ||
            (loadingModelId === null && isSelected));

        return (
          <ModelTooltip description={t(model.description || "")}>
            <button
              onClick={() => !isLoading && onModelChange(model.id)}
              className={`
                  relative flex-1 px-4 py-1 rounded-full border transition-all duration-200 ease-in-out
                  text-left group w-full
                  ${
                    isSelected
                      ? "bg-black dark:bg-white text-white dark:text-black border-black dark:border-white shadow-sm"
                      : "bg-white dark:bg-dark-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-dark-400 hover:border-white dark:hover:border-gray-600 hover:shadow-sm"
                  }
                  ${isLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                  ${isSelected ? "ring-1 ring-black dark:ring-white ring-opacity-20" : ""}
                `}
              role="option"
              aria-selected={isSelected}
              disabled={isLoading}
            >
              <div className="flex items-center gap-1 h-full">
                {/* Placeholder for provider logo - you can replace this with actual logos */}
                <div
                  className={`
                    w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0
                  `}
                >
                  {isCurrentLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : model.image ? (
                    <img
                      src={model.image}
                      alt={model.name}
                      className={`w-6 h-6 object-contain ${!isSelected && model.isSelectedReverseImageColor ? "invert" : ""}`}
                    />
                  ) : (
                    <model.icon className="w-4 h-4" />
                  )}
                </div>

                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <div className="font-medium text-sm truncate">
                    {model.name}
                  </div>
                </div>
              </div>
            </button>
          </ModelTooltip>
        );
      })}
    </div>
  );
}
